/**
 * The affiliate portal's intro tour, in the affiliate's own language.
 *
 * Its own small catalogue rather than more of `messages.ts`: the tour's words
 * change together, when a step is added and the tour's version raised
 * (`tour.ts`), and a test checks this file on its own for all three
 * languages. The controls (Skip tour, Exit tour, Next) are portal-kit's.
 */
import { type Catalogue, type Params, type Translator, tabTranslator } from '@affdice/portal-kit';

export const AFFILIATE_TOUR_MESSAGES = {
  'intro.go': { en: 'Open {screen}', es: 'Abrir {screen}', 'pt-BR': 'Abrir {screen}' },
  // Beside the affiliate's name in the tour's miniature: the portal has no
  // roles, so the role is the kind of account (owner, 2026-10-12).
  'intro.role': { en: 'Affiliate', es: 'Afiliado', 'pt-BR': 'Afiliado' },

  'intro.welcome.title': {
    en: 'Welcome to KD Affiliate',
    es: 'Te damos la bienvenida a KD Affiliate',
    'pt-BR': 'Boas-vindas ao KD Affiliate',
  },
  'intro.welcome.body': {
    en:
      'This is where you promote the programmes you have joined and see what ' +
      'you earn from them. This short tour shows where each part lives.',
    es:
      'Aquí promocionas los programas a los que te has unido y ves lo que ganas ' +
      'con ellos. Este breve recorrido muestra dónde está cada parte.',
    'pt-BR':
      'Aqui você divulga os programas de que participa e vê quanto ganha com ' +
      'eles. Este tour rápido mostra onde fica cada parte.',
  },

  'intro.links.title': {
    en: 'Links and sub-IDs',
    es: 'Enlaces y sub-IDs',
    'pt-BR': 'Links e sub-IDs',
  },
  'intro.links.body': {
    en:
      'Make a tracking link for any campaign you are on — Campaigns shows what ' +
      'each one pays. Add your own labels (sub-IDs) to a link to tell your ' +
      'traffic sources apart; Sources breaks your results down by them.',
    es:
      'Crea un enlace de seguimiento para cualquier campaña en la que estés; ' +
      'Campañas muestra lo que paga cada una. Añade tus propias etiquetas ' +
      '(sub-IDs) a un enlace para distinguir tus fuentes de tráfico; Fuentes ' +
      'desglosa tus resultados por ellas.',
    'pt-BR':
      'Crie um link de rastreamento para qualquer campanha de que você ' +
      'participa; Campanhas mostra quanto cada uma paga. Adicione seus próprios ' +
      'rótulos (sub-IDs) a um link para separar suas origens de tráfego; ' +
      'Origens detalha seus resultados por eles.',
  },

  'intro.creatives.title': {
    en: 'Creatives',
    es: 'Creatividades',
    'pt-BR': 'Criativos',
  },
  'intro.creatives.body': {
    en:
      'Banners and other artwork the brands have published, under Links → ' +
      'Creatives. Copy a creative’s code and it already carries your own link ' +
      'for that campaign; if you have no link there yet, the card says so.',
    es:
      'Banners y otros diseños que han publicado las marcas, en Enlaces → ' +
      'Creatividades. Copia el código de una creatividad y ya lleva tu propio ' +
      'enlace de esa campaña; si todavía no tienes enlace en ella, la tarjeta lo ' +
      'indica.',
    'pt-BR':
      'Banners e outras artes que as marcas publicaram, em Links → Criativos. ' +
      'Copie o código de um criativo e ele já leva o seu próprio link daquela ' +
      'campanha; se você ainda não tem link nela, o cartão avisa.',
  },

  'intro.postbacks.body': {
    en:
      'Tell your own tracker what the players you sent go on to do. Add a ' +
      'postback address, choose the events it should hear about, and press Send ' +
      'test before you rely on it.',
    es:
      'Informa a tu propio rastreador de lo que hacen después los jugadores que ' +
      'enviaste. Añade una dirección de postback, elige los eventos de los que ' +
      'debe enterarse y pulsa Enviar prueba antes de confiar en ella.',
    'pt-BR':
      'Avise o seu próprio rastreador sobre o que os jogadores que você enviou ' +
      'fazem depois. Adicione um endereço de postback, escolha os eventos que ' +
      'ele deve receber e clique em Enviar teste antes de confiar nele.',
  },

  'intro.reports.body': {
    en:
      'Every report in one place — by campaign, sub-ID, country, device and ' +
      'more — for any date range, and ready to export.',
    es:
      'Todos los informes en un solo lugar (por campaña, sub-ID, país, ' +
      'dispositivo y más) para cualquier rango de fechas, y listos para ' +
      'exportar.',
    'pt-BR':
      'Todos os relatórios em um só lugar (por campanha, sub-ID, país, ' +
      'dispositivo e mais) para qualquer período, e prontos para exportar.',
  },

  'intro.statements.body': {
    en:
      'A statement is issued for every period once it is locked, with the lines ' +
      'behind what you earned. Commission shows what is building up in the ' +
      'current period; Invoices shows what is being paid.',
    es:
      'Se emite un extracto por cada periodo en cuanto se bloquea, con las ' +
      'líneas que explican lo que ganaste. Comisión muestra lo que se va ' +
      'acumulando en el periodo actual; Facturas muestra lo que se está pagando.',
    'pt-BR':
      'Um extrato é emitido para cada período assim que ele é travado, com as ' +
      'linhas que explicam o que você ganhou. Comissão mostra o que está se ' +
      'acumulando no período atual; Faturas mostra o que está sendo pago.',
  },

  'intro.done.title': {
    en: 'That’s the tour',
    es: 'Fin del recorrido',
    'pt-BR': 'Fim do tour',
  },
  'intro.done.body': {
    en:
      'Open it again any time from Platform tour at the foot of the left panel. ' +
      'When a new part of the portal is added, that entry is highlighted until ' +
      'you open the tour again.',
    es:
      'Vuelve a abrirlo cuando quieras desde Recorrido por la plataforma, al pie ' +
      'del panel izquierdo. Cuando se añada una parte nueva al portal, esa ' +
      'entrada se resaltará hasta que vuelvas a abrir el recorrido.',
    'pt-BR':
      'Abra de novo quando quiser em Tour pela plataforma, no rodapé do painel ' +
      'esquerdo. Quando uma parte nova for adicionada ao portal, essa entrada ' +
      'fica destacada até você abrir o tour de novo.',
  },

  'intro.figure.alt.welcome': {
    en: 'Layout of the portal with every part of the left panel in view',
    es: 'Disposición del portal con todo el panel izquierdo a la vista',
    'pt-BR': 'Layout do portal com todo o painel esquerdo à vista',
  },
  'intro.figure.alt.links': {
    en: 'Layout of the Links screen, with Links highlighted in the left panel',
    es: 'Disposición de la pantalla Enlaces, con Enlaces resaltado en el panel izquierdo',
    'pt-BR': 'Layout da tela Links, com Links destacado no painel esquerdo',
  },
  'intro.figure.alt.creatives': {
    en: 'Layout of the Creatives section under Links, highlighted in the left panel',
    es: 'Disposición de la sección Creatividades de Enlaces, resaltada en el panel izquierdo',
    'pt-BR': 'Layout da seção Criativos de Links, destacada no painel esquerdo',
  },
  'intro.figure.alt.postbacks': {
    en: 'Layout of the Postbacks screen, with Postbacks highlighted in the left panel',
    es: 'Disposición de la pantalla Postbacks, con Postbacks resaltado en el panel izquierdo',
    'pt-BR': 'Layout da tela Postbacks, com Postbacks destacado no painel esquerdo',
  },
  'intro.figure.alt.reports': {
    en: 'Layout of the Reports screen, with Reports highlighted in the left panel',
    es: 'Disposición de la pantalla Informes, con Informes resaltado en el panel izquierdo',
    'pt-BR': 'Layout da tela Relatórios, com Relatórios destacado no painel esquerdo',
  },
  'intro.figure.alt.statements': {
    en: 'Layout of the Statements screen, with Statements, Commission and Invoices highlighted in the left panel',
    es: 'Disposición de la pantalla Extractos, con Extractos, Comisión y Facturas resaltados en el panel izquierdo',
    'pt-BR': 'Layout da tela Extratos, com Extratos, Comissão e Faturas destacados no painel esquerdo',
  },
  'intro.figure.alt.done': {
    en: 'Layout of the left panel with Platform tour highlighted at its foot',
    es: 'Disposición del panel izquierdo con Recorrido por la plataforma resaltado al pie',
    'pt-BR': 'Layout do painel esquerdo com Tour pela plataforma destacado no rodapé',
  },
} as const satisfies Catalogue;

export type AffiliateTourMessageKey = keyof typeof AFFILIATE_TOUR_MESSAGES;

const cache = new Map<string, Translator<typeof AFFILIATE_TOUR_MESSAGES>>();

/** One tour string, in whatever language this tab is reading. */
export function tourText(key: AffiliateTourMessageKey, params?: Params): string {
  return tabTranslator(AFFILIATE_TOUR_MESSAGES, cache)(key, params);
}
